import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, MoveUpRight, ShoppingCart, Sparkles, Tag } from 'lucide-react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { AnimatePresence, motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import { formatTND } from '../../lib/currency';
import { localizePath } from '../../lib/localizedRoutes';
import { getActivePromo, getEffectivePrice, sortItemsByPromo } from '../../lib/promo';
import { shouldShowPrices } from '../../lib/priceVisibility';

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

function normalizePromoSurfaceItem(item, language, entityType) {
  const activePromo = getActivePromo(item, language);
  if (!activePromo) return null;

  const id = String(item?._id || item?.id || '').trim();
  if (!id) return null;

  const title = String(item?.name || '').trim();
  const image = item?.images?.[0] || '/placeholders/1.webp';
  const route = entityType === 'product' ? `/machines/${id}` : `/spare-parts/${id}`;
  const basePrice = Number(item?.price ?? 0);
  const effectivePrice = getEffectivePrice(item, language);

  return {
    id,
    entityType,
    title,
    image,
    route,
    category:
      entityType === 'product'
        ? String(item?.productType || '').trim()
        : String(item?.category || '').trim(),
    activePromo,
    basePrice,
    effectivePrice,
    hasDiscount: activePromo?.kind === 'price' && effectivePrice < basePrice,
  };
}

function formatSurfaceLabel(value = '') {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const containerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const loadingSwapVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    y: -6,
    transition: { duration: 0.22, ease: 'easeIn' },
  },
};

const promoLoadVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.06,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const promoLoadItemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const sectionShineVariants = {
  hidden: { opacity: 0, x: '-45%' },
  visible: {
    opacity: [0, 0.32, 0.16, 0],
    x: '165%',
    transition: {
      duration: 9,
      repeat: Infinity,
      repeatDelay: 3,
      ease: 'easeInOut',
    },
  },
};

const cardShineVariants = {
  hidden: { opacity: 0, x: '-35%' },
  visible: {
    opacity: [0, 0.24, 0.12, 0],
    x: '145%',
    transition: {
      duration: 8,
      repeat: Infinity,
      repeatDelay: 4,
      ease: 'easeInOut',
    },
  },
};

function PromoCard({ item, language, t, showPrices, featured = false }) {
  const typeLabel =
    item.entityType === 'product' ? t('home.promotions.productLabel') : t('home.promotions.sparePartLabel');

  const cardClassName = featured
    ? 'group relative block w-full overflow-hidden rounded-[30px] border border-white/10 bg-[var(--dash-black)] text-white transition-all duration-300 hover:border-[var(--dash-main)]/40 sm:grid sm:min-h-[240px] sm:grid-cols-[minmax(0,0.6fr)_minmax(0,0.4fr)] md:min-h-[380px] md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'
    : 'group relative block w-full overflow-hidden rounded-[26px] border border-white/10 bg-[var(--dash-black)] text-white transition-all duration-300 hover:border-[var(--dash-main)]/40 sm:grid sm:min-h-[220px] sm:grid-cols-[minmax(0,0.56fr)_minmax(0,0.44fr)] md:min-h-[280px] md:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]';

  const imageClassName = featured
    ? 'relative aspect-[16/10] overflow-hidden bg-[var(--dash-black)] sm:aspect-auto sm:min-h-full md:min-h-full'
    : 'relative aspect-[16/11] overflow-hidden bg-[var(--dash-black)] sm:aspect-auto sm:min-h-full md:min-h-full';

  const contentClassName = featured
    ? 'flex flex-1 flex-col gap-4 bg-[var(--dash-black)] p-4 sm:p-5 md:p-6'
    : 'flex flex-1 flex-col gap-4 bg-[var(--dash-black)] p-4 sm:p-5 md:p-5';

  return (
    <Link
      href={localizePath(item.route, language)}
      className={cardClassName}
    >
      <div className={imageClassName}>
        <ImageOnlySkeletonLoader
          image={item.image}
          mode="native"
          alt={item.title}
          containerClassName="h-full w-full"
          imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-black/75 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-md">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--dash-main)]" />
          {typeLabel}
        </span>
      </div>

      <div className={contentClassName}>
        <div className="space-y-2">
          {item.category ? (
            <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 bg-white/8 px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm">
              <Tag className="h-3.5 w-3.5 shrink-0 text-[var(--dash-main)]" />
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.22em] text-white/75">
                {formatSurfaceLabel(item.category)}
              </span>
            </div>
          ) : null}
          <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-white md:text-xl">
            {item.title}
          </h3>
        </div>

        <div className="mt-auto space-y-3">
          {item.entityType === 'product' ? (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">
                {t('home.promotions.quoteOnRequest')}
              </p>
              <p className="text-base font-bold text-[var(--dash-other)]">
                {t('home.promotions.tailoredOffer')}
              </p>
            </div>
          ) : showPrices ? (
            item.hasDiscount ? (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40 line-through">
                  {formatTND(item.basePrice)}
                </p>
                <p className="text-base font-bold text-[var(--dash-other)]">
                  {formatTND(item.effectivePrice)}
                </p>
              </div>
            ) : (
              <p className="text-base font-bold text-[var(--dash-other)]">
                {formatTND(item.basePrice)}
              </p>
            )
          ) : (
            <p className="text-base font-bold text-[var(--dash-other)]">
              {t('common.priceOnRequest')}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
              <Sparkles className="h-3.5 w-3.5 text-[var(--dash-main)]" />
              {t('home.promotions.viewOffer')}
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--dash-main)] text-white transition-all duration-300 group-hover:bg-[var(--dash-other)] group-hover:text-[var(--dash-black)]">
              {item.entityType === 'product' ? <MoveUpRight className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function HomePromotionsSection() {
  const { language, t } = useI18n();
  const [products, setProducts] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const swiperRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    Promise.all([
      fetchJsonOrThrow(`/api/products?lang=${language}&paginated=false`),
      fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=false`),
    ])
      .then(([productsData, sparePartsData]) => {
        if (!isMounted) return;
        setProducts(Array.isArray(productsData) ? productsData : []);
        setSpareParts(Array.isArray(sparePartsData) ? sparePartsData : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setProducts([]);
        setSpareParts([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [language]);

  const promoItems = useMemo(() => {
    const combined = [
      ...sortItemsByPromo(
        products.map((item) => normalizePromoSurfaceItem(item, language, 'product')).filter(Boolean),
      ),
      ...sortItemsByPromo(
        spareParts.map((item) => normalizePromoSurfaceItem(item, language, 'sparepart')).filter(Boolean),
      ),
    ];

    return sortItemsByPromo(combined).slice(0, 4);
  }, [language, products, spareParts]);
  const promoItemsKey = promoItems.map((item) => `${item.entityType}-${item.id}`).join('|');

  const titleParts = useMemo(() => {
    const line1 = String(t('home.promotions.titleLine1') || '').trim();
    const line2 = String(t('home.promotions.titleLine2') || '').trim();
    return { line1, line2 };
  }, [t]);
  const showPrices = shouldShowPrices();

  const handlePrevClick = () => {
    if (!swiperRef.current || promoItems.length <= 1) return;
    swiperRef.current.slidePrev();
  };

  const handleNextClick = () => {
    if (!swiperRef.current || promoItems.length <= 1) return;
    swiperRef.current.slideNext();
  };

  return (
    <section className="relative overflow-hidden bg-[var(--dash-other)] py-24 text-[var(--dash-black)]">
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.45)_0%,transparent_36%),radial-gradient(circle_at_85%_10%,rgba(255,93,16,0.14)_0%,transparent_28%)]" />


      <motion.div
        className="container relative z-10 mx-auto px-6 lg:px-12"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-120px' }}
      >
        <motion.div
          key={language}
          className="mb-10 text-center"
          variants={itemVariants}
        >
          <motion.div variants={itemVariants} className="flex justify-center">
            <AnimatedEyebrow
              className="mb-6"
              borderClassName="border-[var(--dash-main)]/40"
              surfaceClassName="bg-[var(--dash-black)]"
              dotClassName="bg-[var(--dash-main)] shadow-[0_0_10px_rgba(228,188,155,0.7)]"
            >
              {t('home.promotions.eyebrow')}
            </AnimatedEyebrow>
          </motion.div>
          <h2 className="mx-auto mt-4 max-w-xl text-3xl font-light uppercase !leading-[1.2] tracking-tight text-[var(--dash-black)] sm:text-4xl md:text-5xl xl:max-w-none">
              <span className="block xl:inline">{titleParts.line1}</span>
              <span className="block font-black xl:inline xl:ml-3">{titleParts.line2}</span>
          </h2>
          <motion.div className="mt-5 space-y-4" variants={itemVariants}>
            <p className="mx-auto max-w-3xl text-md leading-relaxed text-[var(--dash-black)]/75">
              {t(
                'home.promotions.description',
                'Active offers are highlighted here while they last, so you can act quickly on the most relevant machines and spare parts.',
              )}
            </p>
            <div className="flex justify-center">
              <Link
                href={localizePath('/promotions', language)}
                className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-[var(--dash-main)] px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[var(--dash-black)] hover:text-white"
              >
                {t('home.promotions.bannerButton')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={itemVariants}
        >
          <AnimatePresence mode="wait" initial={false}>
          {isLoading ? (
            <motion.div
              key="promo-loading"
              className="grid gap-5 md:grid-cols-2"
              variants={loadingSwapVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {Array.from({ length: 2 }).map((_, index) => (
                <motion.div key={index} variants={itemVariants} className="h-72 animate-pulse rounded-[26px] border border-black/10 bg-white/40" />
              ))}
            </motion.div>
          ) : promoItems.length > 0 ? (
            promoItems.length === 1 ? (
            <motion.div
              key={promoItemsKey || 'promo-single'}
              className="w-full"
              variants={promoLoadItemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <PromoCard item={promoItems[0]} language={language} t={t} showPrices={showPrices} featured />
            </motion.div>
          ) : (
            <motion.div
              key={promoItemsKey || 'promo-carousel'}
              className="relative"
              variants={promoLoadVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
                <Swiper
                  className="home-promotions-swiper"
                  modules={[Autoplay, Pagination]}
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  slidesPerView={1}
                  spaceBetween={20}
                  breakpoints={{
                    1024: {
                      slidesPerView: 2,
                    },
                  }}
                  loop={promoItems.length > 2}
                  autoplay={
                    promoItems.length > 2
                      ? {
                          delay: 4500,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }
                      : false
                  }
                  pagination={false}
                  watchOverflow
                >
                  {promoItems.map((item) => (
                    <SwiperSlide key={`${item.entityType}-${item.id}`} className="h-auto">
                      <motion.div variants={promoLoadItemVariants} className="h-full">
                        <PromoCard item={item} language={language} t={t} showPrices={showPrices} />
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <motion.div className="mt-8 flex items-center justify-center gap-3" variants={promoLoadItemVariants}>
                  <button
                    type="button"
                    onClick={handlePrevClick}
                    className="home-promotions-prev group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white/60 shadow-sm"
                    aria-label={t('common.previous')}
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-black/5 transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-main)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronLeft className="relative z-10 h-6 w-6 text-[var(--dash-black)] transition-colors group-hover:text-white" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextClick}
                    className="home-promotions-next group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white/60 shadow-sm"
                    aria-label={t('common.next')}
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-black/5 transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-main)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronRight className="relative z-10 h-6 w-6 text-[var(--dash-black)] transition-colors group-hover:text-white" />
                  </button>
                </motion.div>
              </motion.div>
            )
          ) : (
            <motion.div
              key="promo-empty"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="rounded-[28px] border border-dashed border-black/10 bg-white/35 p-10 text-center text-[var(--dash-black)]"
            >
              <p className="text-lg font-semibold">{t('home.promotions.emptyTitle')}</p>
              <p className="mt-2 text-sm text-[var(--dash-black)]/70">
                {t('home.promotions.emptyDescription')}
              </p>
            </motion.div>
          )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </section>
  );
}
